import React, { useCallback, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Skeleton,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { LocalHospital, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { medicalLeaveService } from '../../../services/medicalLeaveService';
import type { MedicalLeave } from '../../../types/medicalLeave';

interface EmployeeCardProps {
  employeeId: number;
  companyId: number;
}

const ACCENT = '#E65100';
const PAGE_SIZE = 5;

export const EmployeeMedicalLeavesCard: React.FC<EmployeeCardProps> = ({ employeeId }) => {
  const [page, setPage] = useState(0);

  // Afastamento tem permissão própria, e Recepção e Conferente não a têm.
  const carregar = useCallback(
    () =>
      medicalLeaveService
        .getMedicalLeaves({ employeeId, limit: 1000, sortBy: 'startDate', sortOrder: 'DESC' })
        .then((res) => res.medicalLeaves ?? []),
    [employeeId]
  );
  const lista = useListaPermitida<MedicalLeave>('medical_leaves.read', 'Afastamentos', carregar);
  const leaves = lista.itens;
  const loading = lista.carregando;

  const totalDays = leaves.reduce((sum, l) => sum + (l.totalDays || 0), 0);

  const formatDate = (d: string) => {
    try { return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR'); } catch { return d; }
  };

  const totalPages = Math.ceil(leaves.length / PAGE_SIZE);
  const paged = leaves.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><LocalHospital fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Licenças Médicas
          </Typography>
          {!loading && !lista.erro && (
            <Chip label={leaves.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
        </Box>

        {/* Body */}
        {loading && (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
          </Stack>
        )}

        {lista.erro && (
          <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{lista.erro}</Alert>
        )}

        {!loading && !lista.erro && leaves.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {lista.bloqueio ?? 'Nenhum registro encontrado'}
          </Typography>
        )}

        {!loading && !lista.erro && leaves.length > 0 && (
          <>
            {/* Summary */}
            <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
              <Chip label={`${leaves.length} licença${leaves.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
              <Chip label={`${totalDays} dia${totalDays !== 1 ? 's' : ''} afastado`} size="small" variant="outlined" color="warning" />
            </Box>

            {/* List */}
            <Stack spacing={0.75}>
              {paged.map((l) => (
                <Box
                  key={l.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                >
                  <Typography variant="caption" sx={{ minWidth: 100 }}>
                    {formatDate(l.startDate)} - {formatDate(l.endDate)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap title={l.cidCode}>
                    {l.cidCode}
                  </Typography>
                  <Chip
                    label={`${l.totalDays}d`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              ))}
            </Stack>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, leaves.length)} de {leaves.length}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
